import React from 'react';

import { cn } from 'lib/helpers/utility';
import useLuminance from 'lib/hooks/useLuminance';

import Icon from 'components/shared/Icon/Icon';
import Avatar from 'components/shared/Avatar/Avatar';

import { getClasses } from './lib/getClasses';

/**
 * @param {import('./lib/propTypes').TagProps} TagProps
 */

const Tag = ({
  children,
  icon,
  iconColor,
  avatar,
  color = 'neutral',
  borderColor,
  onRemove,
  removeValue,
  close = false,
  size = 'small',
  variant = 'light',
  rightIcon,
  className,
  labelClassName,
  truncate = false,
  ...rest
}) => {
  const { height, spacing, iconSize, labelClasses, colorClasses, style } = getClasses(
    size,
    variant,
    color
  );

  // Only hex backgrounds can be arbitrarily dark; color tokens always render a light shade.
  const { isDark } = useLuminance(color);
  const contentColor = isDark ? 'white' : undefined;

  return (
    <div
      className={cn(
        'flex w-fit max-w-full items-center gap-1 rounded border border-solid',
        borderColor ? 'border-transparent' : 'border-black !border-opacity-[12%]',
        height,
        spacing,
        colorClasses,
        className
      )}
      style={borderColor ? { ...style, borderColor } : style}
      {...rest}>
      <div className="flex min-w-0 items-center gap-1">
        {icon && <Icon icon={icon} size={iconSize} color={iconColor ?? contentColor} />}
        {avatar && <Avatar size="small" isSquare={false} {...avatar} />}
        <span
          className={cn(
            'min-w-0 font-normal',
            labelClasses,
            `text-${contentColor ?? 'neutral-900'}`,
            truncate && 'truncate',
            labelClassName
          )}>
          {children}
        </span>
        {rightIcon && (
          <Icon icon={rightIcon} size={iconSize} color={contentColor} className="cursor-pointer" />
        )}
      </div>
      {close && (
        <Icon
          icon="close-linear"
          className="cursor-pointer"
          size={iconSize}
          color={contentColor}
          onClick={(e) => {
            e?.stopPropagation();
            onRemove(removeValue !== undefined ? removeValue : children);
          }}
        />
      )}
    </div>
  );
};

export default Tag;
